/* eslint-disable no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {
  AddToTrolley,
  AddToWishlist,
  Image,
  SpecCardSkeleton,
  ProductCardSkeleton,
  ErrorBoundary
} from '@sainsburys-tech/bolt'
import { TuLogo } from '@sainsburys-tech/bolt-icons'
import { trackWishlist } from '../../helpers/TaggingSerializer'
import { contentAreaTypes } from '../../config'
import Templates from './ProductList.template'
import CategoryListSkeleton from '../CategoryList/CategoryListSkeleton'
import * as Styled from './styles.js'
import { getValue } from '../../utils/ConfigProvider'

const CMS = loadable.lib(() => import(/* webpackChunkName: "cms-components" */ '@sainsburys-tech/bolt-cms-components'))

class ProductList extends React.PureComponent {
  getProductCardLayout(templateType, pageType) {
    if (getValue('features.productList.forceClothingTemplate')) {
      return Templates.clothingLayout
    }

    if (pageType === Templates.pageTypeBrowse) {
      if (templateType === Templates.fashionTemplate) {
        return Templates.clothingLayout
      }
      if (templateType === Templates.whiteGoodTemplate) {
        return Templates.whiteGoodTemplate
      }
    }
    return Templates.defaultTemplate
  }

  buildQueryParamTracking(partNumber, searchTerm, currentPage, pageSize, index, recall, relevancyRank, isClearance) {
    const position = pageSize * (currentPage - 1) + index
    return searchTerm
      ? `/product/${partNumber}?clickSR=slp:term:${searchTerm}:${position}:${recall}:${relevancyRank}`
      : `/product/${partNumber}?clickPR=${!isClearance ? 'plp' : 'clearance'}:${position}:${recall}`
  }

  getBrandLogo(layout, pageType, brand) {
    // Get the opt-in feature status
    const additionalBrandLogos = getValue('features.productList.additionalBrandLogos')

    // If the feature value is an array, find a matching logo for the brand.
    if (Array.isArray(additionalBrandLogos)) {
      // The brand name may have different casing to our data, so normalise while searching.
      const brandLogo = additionalBrandLogos.find(
        ({ name }) => String(brand).toLowerCase() === String(name).toLowerCase()
      )

      // If there's a brand in the additionalBrandLogos, and it's got a logo URL, return a BrandLogo
      if (brandLogo && brandLogo.logoUrl) {
        return <Image alt={brand} src={encodeURI(brandLogo.logoUrl)} height={20} />
      }
    }

    // For clothing layouts, apply custom brand logo logic
    if (layout === Templates.clothingLayout) {
      // Display brands that aren't Tu as their brand name
      if (brand !== 'Tu') {
        return brand
      }

      // Don't show the Tu logo for Tu brand products, if it's disabled in features
      if (!getValue('features.productList.disableTuLogo')) {
        return <TuLogo height={24} width={24} />
      }
    }

    // If this is a Habitat browse page, give every product the Habitat logo
    if (pageType === 'browse' && brand === 'Habitat') {
      return <Styled.HabitatLogo height={26} width={60} />
    }

    // Return no brand logo for all other scenarios
    return null
  }

  populateSpecificationData(attributes) {
    return {
      heading: Templates.specificationHeader,
      secondaryData: Templates.secondaryDataArray(attributes),
      headerLink: (
        <>
          <a href={`/product/${attributes.partNumber}`}>{Templates.headerText}</a>
        </>
      ),
      overviewData: { content: attributes.detailAttributes },
      smallTitle: true
    }
  }

  isExcluded(isDeliverable, isReservable, hasVariations) {
    return !isDeliverable && !isReservable && !hasVariations
  }

  alternativeImagesConditionChecker(templateType, pageType) {
    if (getValue('features.productList.forceEnableAltImageHover')) {
      return true
    }

    return (
      templateType && templateType.toLowerCase() !== Templates.defaultTemplate && pageType === Templates.pageTypeBrowse
    )
  }

  showSkeleton(showLoader, attributes) {
    return showLoader || typeof attributes.price !== 'number'
  }

  isLazyLoadImage(position) {
    return position > 6
  }

  render() {
    const {
      showLoader,
      templateType,
      pageType,
      destinationPageType,
      searchTerm,
      numberOfResults,
      currentPage,
      pageSize,
      products,
      relavancyRecallOffset,
      userState,
      hookLogic,
      isClearance,
      isCannedSearch
    } = this.props

    const layout = this.getProductCardLayout(templateType, pageType)
    const imageVariant = this.alternativeImagesConditionChecker(templateType, pageType)
    const whiteGoodCategory = layout === Templates.whiteGoodTemplate
    const productCardClass = 'xs-6--none sm-4'

    const handleAddToTrolleyClick = (event, partNumber, productInfo, positionInList, clickOrigin) =>
      AddToTrolley.onAddToTrolleyModalOpen(productInfo)(clickOrigin, positionInList)()
    const className = whiteGoodCategory ? 'xs-block xs-12--none md-12' : productCardClass
    let recallPosition = relavancyRecallOffset || 0
    const Skeleton = () => (
      <Styled.SkeletonWrapper className={className}>
        <ProductCardSkeleton isPortrait={layout === Templates.clothingLayout} />
      </Styled.SkeletonWrapper>
    )
    const SpecSkeleton = () => (
      <Styled.SkeletonWrapper whiteGoods className={className}>
        <SpecCardSkeleton />
      </Styled.SkeletonWrapper>
    )
    const ProductCard = loadable(() => import(/* webpackChunkName: "labs-product-card" */ './StyledProductCard.js'), {
      fallback: <Skeleton />
    })

    const SpecCard = loadable(() => import(/* webpackChunkName: "spec-card" */ './SpecCard.js'), {
      fallback: <SpecSkeleton />
    })

    return (
      <ErrorBoundary>
        <Styled.ProductList data-test='product-list'>
          {showLoader && destinationPageType === 'CLP' ? (
            <CategoryListSkeleton isFullSize />
          ) : (
            products.map((product, index) => {
              if (product.type === contentAreaTypes.ContentInList) {
                return !showLoader ? (
                  <ErrorBoundary>
                    <CMS>
                      {({ components }) => {
                        const { M053 } = components
                        return (
                          <Styled.LazyHydrateCard whenVisible style={{ display: 'flex' }} className={className}>
                            <ProductCard as='div' isM053 data-test='product-list-m053' key={`M053-${index}`}>
                              <M053 heading={product.heading} image={product.image} link={product.link} />
                            </ProductCard>
                          </Styled.LazyHydrateCard>
                        )
                      }}
                    </CMS>
                  </ErrorBoundary>
                ) : !whiteGoodCategory ? (
                  <Skeleton />
                ) : (
                  <SpecSkeleton />
                )
              }
              const partNumber = product.id
              const attributes = { ...product.attributes, partNumber }

              const trolleyOnClick = (event, partNumber) => {
                const productInfo = [{ id: partNumber, price: attributes.price.toString(), quantity: 1 }]
                const clickOrigin =
                  pageType === Templates.pageTypeBrowse
                    ? Templates.browseAddToTrolleyOrigin
                    : Templates.searchAddToTrolleyOrigin
                handleAddToTrolleyClick(event, partNumber, productInfo, index + 1, clickOrigin)
                this.props.sendHookLogicClickBeacon(attributes, hookLogic, partNumber)
              }
              const productInfoOnly = this.isExcluded(
                attributes.deliverable,
                attributes.reservable,
                attributes.hasVariations
              )
              this.props.sendHookLogicImpressionBeacon(attributes, hookLogic)
              const colorVariants = attributes.variants && attributes.variants.Colour
              recallPosition = !attributes.featured ? (recallPosition += 1) : recallPosition
              const isLazy = this.isLazyLoadImage(recallPosition)
              return (
                <ErrorBoundary key={`${partNumber}-${index}-boundary`}>
                  {!whiteGoodCategory ? (
                    !this.showSkeleton(showLoader, attributes) ? (
                      <Styled.LazyHydrateCard whenVisible style={{ display: 'flex' }} className={className}>
                        <ProductCard
                          lazyImageLoad={isLazy}
                          key={`${partNumber}${index}`}
                          isPortrait={layout === Templates.clothingLayout}
                          partNumber={partNumber}
                          imageHoverActive={imageVariant}
                          colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
                          brand={this.getBrandLogo(layout, pageType, attributes.brand)}
                          featured={attributes.featured} // TODO: FEATURED
                          name={attributes.name}
                          price={attributes.price}
                          wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
                          specialOfferText={attributes.specialOfferText}
                          rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
                          reviewsCount={attributes.reviewsCount}
                          productLinkOverride={
                            !attributes.productLink && !isCannedSearch
                              ? this.buildQueryParamTracking(
                                  partNumber,
                                  searchTerm,
                                  currentPage,
                                  pageSize,
                                  recallPosition,
                                  numberOfResults,
                                  attributes.relevancyRank,
                                  isClearance
                                )
                              : attributes.productLink
                          }
                          badge={attributes.badge}
                          freeDelivery={attributes.freeDelivery}
                          hasVariations={attributes.hasVariations}
                          hasMultiplePrices={attributes.hasMultiplePrices}
                          productInfoOnly={productInfoOnly}
                          buttonLabel={
                            attributes.hasVariations && (productInfoOnly ? 'More Details' : 'Choose Options')
                          }
                          turnOffCta={getValue('features.productCard.turnOffCta')}
                          cta={
                            <ErrorBoundary>
                              <AddToTrolley.Button
                                className='btn-cta'
                                partNumber={partNumber}
                                openView
                                showIcon
                                onClick={trolleyOnClick}
                              />
                            </ErrorBoundary>
                          }
                          wishlistCta={
                            getValue('features.wishlist.enabled') && (
                              <ErrorBoundary>
                                <AddToWishlist.Button
                                  item={{
                                    partNumber,
                                    hasVariant: attributes.hasVariations
                                  }}
                                  tooltipDurationToShow={10000}
                                  tooltipWidth={260}
                                  onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                                  tooltipPosition='right'
                                />
                              </ErrorBoundary>
                            )
                          }
                          colorSwatchInline={getValue('features.productCard.colorSwatchInline')}
                          colorSwatchAlwaysVisible={getValue('features.productCard.colorSwatchAlwaysVisible')}
                          colorSwatchMaxVariations={getValue('features.productCard.colorSwatchMaxVariations')}
                          colorSwatchSize={getValue('features.productCard.colorSwatchSize')}
                        />
                      </Styled.LazyHydrateCard>
                    ) : (
                      <Skeleton />
                    )
                  ) : !this.showSkeleton(showLoader, attributes) ? (
                    <Styled.WhiteGoodsWrapper className={className}>
                      <Styled.LazyHydrateCard whenVisible>
                        <SpecCard
                          key={`${partNumber}${index}`}
                          isPortrait={layout === Templates.clothingLayout}
                          partNumber={partNumber}
                          imageHoverActive={imageVariant}
                          colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
                          brand={this.getBrandLogo(layout, pageType, attributes.brand)}
                          featured={attributes.featured} // TODO: FEATURED
                          name={attributes.name}
                          price={attributes.price}
                          wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
                          specialOfferText={attributes.specialOfferText}
                          rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
                          reviewsCount={attributes.reviewsCount}
                          productLinkOverride={
                            !attributes.productLink && !isCannedSearch
                              ? this.buildQueryParamTracking(
                                  partNumber,
                                  searchTerm,
                                  currentPage,
                                  pageSize,
                                  recallPosition,
                                  numberOfResults,
                                  attributes.relevancyRank,
                                  isClearance
                                )
                              : attributes.productLink
                          }
                          badge={attributes.badge}
                          freeDelivery={attributes.freeDelivery}
                          hasVariations={attributes.hasVariations}
                          hasMultiplePrices={attributes.hasMultiplePrices}
                          productInfoOnly={productInfoOnly}
                          buttonLabel={productInfoOnly ? 'More Details' : 'Choose Options'}
                          cta={
                            !getValue('features.productCard.turnOffCta') && (
                              <ErrorBoundary>
                                <AddToTrolley.Button className='btn-cta' partNumber={partNumber} openView showIcon />
                              </ErrorBoundary>
                            )
                          }
                          wishlistCta={
                            <ErrorBoundary>
                              <AddToWishlist.Button
                                item={{
                                  partNumber,
                                  hasVariant: attributes.hasVariations
                                }}
                                tooltipDurationToShow={10000}
                                tooltipWidth={260}
                                onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                                tooltipPosition='left'
                              />
                            </ErrorBoundary>
                          }
                          specificationData={this.populateSpecificationData(attributes)}
                        />
                      </Styled.LazyHydrateCard>
                    </Styled.WhiteGoodsWrapper>
                  ) : (
                    <SpecSkeleton />
                  )}
                </ErrorBoundary>
              )
            })
          )}
        </Styled.ProductList>
      </ErrorBoundary>
    )
  }
}

ProductList.propTypes = {
  products: PropTypes.array.isRequired,
  pageType: PropTypes.string,
  templateType: PropTypes.string,
  hookLogic: PropTypes.object.isRequired,
  sendHookLogicImpressionBeacon: PropTypes.func.isRequired,
  userState: PropTypes.object,
  destinationPageType: PropTypes.string,
  searchTerm: PropTypes.string,
  numberOfResults: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  relavancyRecallOffset: PropTypes.number,
  showLoader: PropTypes.bool,
  sendHookLogicClickBeacon: PropTypes.func,
  isClearance: PropTypes.bool,
  isCannedSearch: PropTypes.bool
}

ProductList.defaultProps = {
  searchTerm: '',
  numberOfResults: null,
  currentPage: 1,
  pageSize: 30,
  destinationPageType: '',
  pageType: 'search',
  templateType: '',
  userState: {},
  relavancyRecallOffset: 0,
  showLoader: false,
  sendHookLogicClickBeacon: () => {},
  isClearance: false,
  isCannedSearch: false
}

export default ProductList
